import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { get, isNumber } from 'lodash';
import { setDeviceAntennaDirections } from 'redux/Commissioning';
import { updatePivotOverviewPageRfConfigActionStatus } from 'redux/actions';
import Button from 'components/shared/Button';
import Form from 'components/shared/Form';
import FormTable from 'components/shared/FormTable';
import { ErrorSolidIcon } from 'components/icons';
import CommissionErrorBox from '../Commissioning/CommissionErrorBox/CommissionErrorBox';
import { columns, parameters } from './utils';
import styles from './GA2RFControllerTable.module.css';
import { ProgressIndicator } from 'components/shared/Progress';
import { ERROR_CODES, Status } from 'common/utils/constants';
import WarningBox from 'components/shared/WarningBox/WarningBox';
import { RF_CONTROL_WARNING_MESSAGE } from '../RfController/utils';

function GA2RFControllerTable({ device }) {
  const dispatch = useDispatch();
  const rfConfigStatus = useSelector(
    (state) => state.ui.pivotOverviewPage.actions?.rfConfig?.[device?.deviceId]?.status
  );
  const [applyError, setApplyError] = useState(null);
  const deviceProperties = device?.properties?.reported;
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      azElectrical: '',
      dtElectrical: ''
    }
  });
  const { formState, reset, watch } = methods;
  const { errors } = formState;
  const { isSubmitting } = formState;

  const formAzElectrical = watch('azElectrical');
  const formDtElectrical = watch('dtElectrical');

  const values = useMemo(() => {
    const azElectrical = get(deviceProperties, 'DU.az-el-info.downlink.hpol.azimuth', '');
    const elElectrical = get(deviceProperties, 'DU.az-el-info.downlink.hpol.elevation', '');

    return {
      azElectrical,
      dtElectrical: isNumber(elElectrical) ? elElectrical * -1 : '', // downtilt = -elevation
      range: { min: -45, max: 45 }
    };
  }, [deviceProperties]);
  const meta = useMemo(
    () => ({
      azElectrical: {
        lastUpdated: get(deviceProperties, '$metadata.DU.az-el-info.downlink.hpol.azimuth.$lastUpdated')
      },
      dtElectrical: {
        lastUpdated: get(deviceProperties, '$metadata.DU.az-el-info.downlink.hpol.elevation.$lastUpdated')
      }
    }),
    [deviceProperties]
  );

  const handleSubmit = useCallback(
    async (directions) => {
      dispatch(updatePivotOverviewPageRfConfigActionStatus(device?.deviceId, Status.PENDING));
      const { azElectrical, dtElectrical } = directions;

      let azimuth = parseInt(azElectrical);
      let downtilt = parseInt(dtElectrical);

      // check if either azElectrical or dtElectrical is empty
      // if yes, set it to the previous value
      if (isNaN(azimuth)) {
        azimuth = parseInt(values.azElectrical);
      }

      if (isNaN(downtilt)) {
        downtilt = parseInt(values.dtElectrical);
      }

      const hpol = { azimuth, elevation: downtilt * -1 };

      const newDirections = {
        downlink: {
          hpol,
          vpol: hpol
        }
      };

      await dispatch(setDeviceAntennaDirections(device?.deviceId, newDirections))
        .then((res) => {
          dispatch(updatePivotOverviewPageRfConfigActionStatus(device?.deviceId, Status.SUCCESS));
        })
        .catch((error) => {
          dispatch(updatePivotOverviewPageRfConfigActionStatus(device?.deviceId, Status.FAILURE));
          const errorObj = {
            message:
              error?.message || ERROR_CODES[error.cause?.status] || 'Something went wrong. Please try again later.',
            type: error.cause?.status || 'Unknown'
          };
          setApplyError(errorObj);
        });
    },
    [dispatch, device, values]
  );
  const handleReset = useCallback(
    (event) => {
      event.preventDefault();
      reset();
    },
    [reset]
  );

  useEffect(() => {
    if (formState.isSubmitSuccessful) {
      reset();
    }
  }, [formState, reset]);

  const handleCloseErrorBox = useCallback(() => {
    reset();
    setApplyError(null);
    dispatch(updatePivotOverviewPageRfConfigActionStatus(device?.deviceId, Status.NONE));
  }, [reset, dispatch, device]);

  const disableApplyBtn = useMemo(() => {
    if (errors.azElectrical?.message || errors.dtElectrical?.message || isSubmitting) {
      return true;
    }

    if (formAzElectrical?.trim() === '' && formDtElectrical?.trim() === '') {
      return true;
    }

    return false;
  }, [isSubmitting, formAzElectrical, formDtElectrical, errors.azElectrical?.message, errors.dtElectrical?.message]);

  return (
    <Form methods={methods} onSubmit={handleSubmit} className={styles.root}>
      {rfConfigStatus === Status.PENDING && <ProgressIndicator />}
      {applyError && (
        <CommissionErrorBox
          title={`${applyError.title || 'Set Antenna Direction'}: ${applyError.type}`}
          message={applyError.message}
          onClose={handleCloseErrorBox}
        />
      )}
      <FormTable
        values={values}
        parameters={parameters}
        meta={meta}
        customColumns={columns}
        pageSize={parameters?.length}
      />
      <div className={styles.errorContainer}>
        {(errors?.azElectrical?.message || errors.dtElectrical?.message) && (
          <div className={styles.errorBox}>
            <ErrorSolidIcon className={styles.errorIcon} />
            <span className={styles.errorMsg}>{errors?.azElectrical?.message || errors.dtElectrical?.message}</span>
          </div>
        )}
      </div>
      <div className={styles.warning}>
        <WarningBox warningMessages={RF_CONTROL_WARNING_MESSAGE} />
      </div>
      <div className={styles.actions}>
        <Button type="reset" variant="link" size="medium" color="primary" onClick={handleReset}>
          Clear
        </Button>
        <Button type="submit" variant="contained" size="medium" color="primary" disabled={disableApplyBtn}>
          Apply
        </Button>
      </div>
    </Form>
  );
}

export default React.memo(GA2RFControllerTable);
